import React, { useState, useEffect } from "react";
import useAxios from "axios-hooks";
import {
    Spinner,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    Modal,
    TextField,
    ComboBox,
    Toggle
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import StepsTable from "./StepsTable";
import { Form, Formik } from "formik";
import CustomTextField from "../../../../Forms/TextField";
import CustomToggle from "../../../../Forms/Toggle";

const CustomMenuList = (props) => {
    const height = 35;
    const itemCount = props.children.length;
    const totalHeight = Math.min(300, itemCount * height); // Adjust the max height
  
    return (
      <List
        height={totalHeight}
        itemCount={itemCount}
        itemSize={height}
        width="100%"
      >
        {({ index, style }) => (
          <div
          style={{
            ...style,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 8px",
            boxSizing: "border-box",
          }}
          title={props.children[index]?.props?.children}
        >
          {props.children[index]}
        </div>
        )}
      </List>
    );
  };

const StepsList = ({ crsId, revId, setup }) => {
    const [selectedStepId, setSelectedStepId] = useState(undefined);

    const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);
    const [showCreateModal, { toggle: toggleCreateModal }] = useBoolean(false);
    const [showUpdateModal, { toggle: toggleUpdateModal }] = useBoolean(false);

    const [{ data: course, loading }, refetch] = useAxios(`courses/${crsId}/${revId || ''}`);

    const [{ ordering, error }, execute] = useAxios(
        {
        url: `courses/${crsId}/order-steps`,
        method: 'POST',
        },
        { manual: true } // Set to manual so you can trigger it when needed
    );
    
    const updateStepNumber = async (crsXCRSStepId, newStepNumber) => {
        const response = await execute({
            data: {
                CRSXCRSStepId: crsXCRSStepId,
                StepNumber: newStepNumber,
                CRSRevID: revId,
                Setup: setup
            },
        });
        refetch()
    };
      
    if (loading)
        return <Spinner className="absolute-center" label="Loading data..." />;
    return (
        <>
            <StepsTable
                steps={course.steps.filter(x => x.courseStepCategoryID === (setup ? 1 : 2))}
                setSelectedStepId={setSelectedStepId}
                selectedStepId={selectedStepId}
                createOnClick={() => toggleCreateModal()}
                deleteOnClick={() => toggleDeleteModal()}
                editOnClick={() => toggleUpdateModal()}
                loading={loading}
                updateStepNumber={updateStepNumber}
                setup={setup}
                createDisabled={revId && revId != course.currentCRSRevID}
            />
            <CreateStepsModal
                courseId={crsId}
                course={course}
                showModal={showCreateModal}
                toggleShowModal={toggleCreateModal}
                onCreate={() => refetch()}
                setup={setup}
            />
            <UpdateStepsModal
                courseId={crsId}
                course={course}
                selectedStepId={selectedStepId}
                showModal={showUpdateModal}
                toggleShowModal={toggleUpdateModal}
                onUpdate={() => refetch()}
                setup={setup}
            />
            <DeleteStepsModal
                courseId={crsId}
                selectedStepId={selectedStepId}
                showModal={showDeleteModal}
                toggleShowModal={toggleDeleteModal}
                onDelete={() => refetch()}
            />
        </>
    );
};

const CreateStepsModal = ({
    showModal,
    toggleShowModal,
    onCreate,
    course,
    courseId,
    setup
}) => {
    const [{ data: steps, loadingSteps }] = useAxios(`courses/steps`);
    const [{ data: steptypes, loadingStepTypes }] = useAxios(`courses/steptypes`);

    const [stepOptions, setStepOptions] = useState([]);
    const [navSectionTitles, setNavSectionTitles] = useState([]);

    const [{ loading }, postForm] = useAxios(
        {
            method: "post",
            url: `/courses/${courseId}/steps`,
        },
        { manual: true }
    );
    
    useEffect(() => {
        if (steps) {
            setStepOptions(steps.map((step) => ({
                value: step.id,
                label: step.crsStepTypeID ? ('STEPID: ' + step.id + ' ')  + (step.qnsid ? 'QNSID: ' + step.qnsid + ' ' + (step.name ? step.name : '') : (step.name ? step.name : '')) : '',
              })));
              const distinctTitles = [
                ...new Set(
                  course.steps
                    .map(step => step.navSectionTitle) // Extract navSectionTitle from each step
                    .filter(title => title !== null && title !== undefined) // Filter out null or undefined values
                )
              ];
              setNavSectionTitles(distinctTitles);
        }
    }, [steps]);

    if (loading || loadingSteps)
        return <Spinner className="absolute-center" label="Loading..." />;

    return (
        <Modal
            isOpen={showModal}
            onDismiss={toggleShowModal}
            isBlocking={true}
            styles={{
                main: {
                    width: '500px', // Explicitly set the width
                    maxWidth: '500px', // Ensure it doesn't exceed this width
                    minWidth: '500px', // Ensure it doesn't shrink below this width
                },
            }}
        >
            <div style={{ padding: '20px', height:500 }}>
            <h2 style={{ marginBottom: '10px', marginTop:0, fontSize:24,fontWeight:'400' }}>Create</h2>
            <Formik
                initialValues={{crsStepCategoryID: setup ? 1 : 2}}
                onSubmit={async (values) => {
                    const result = await postForm({ data: values });
                    if (!result.isAxiosError) {
                        onCreate();
                        toggleShowModal();
                    }
                }}
            >
                 {({ setFieldValue, values }) => (
                <Form>
                    <div style={{ marginBottom: "3px" }}>
                    <label style={{fontWeight:'500', display:'block', marginBottom: 5}}>Step Type</label>
                    <Select
                        components={{ MenuList: CustomMenuList }}
                        options={steptypes?.map(x => ({ value: x.crsStepTypeID, label: x.name }))}
                        onChange={(option) => { setFieldValue("crsStepTypeID", option?.value) }}
                        value={steptypes?.map(x => ({ value: x.crsStepTypeID, label: x.name })).find((option) => option.value === values.crsStepTypeID)}
                        placeholder="Select a Step Type"
                        isSearchable
                        className="mb10"
                    />
                    
                    <label style={{ fontWeight: '500', display: 'block', marginBottom: 5 }}>Step</label>
                    <Select
                        components={{ MenuList: CustomMenuList }}
                        options={steps.filter(step => step.crsStepTypeID === values.crsStepTypeID).map((step) => ({
                            value: step.id,
                            label: step.crsStepTypeID ? ('STEPID: ' + step.id + ' ')  + (step.qnsid ? 'QNSID: ' + step.qnsid + ' ' + (step.name ? step.name : '') : (step.name ? step.name : '')) : '',
                          }))}
                        onChange={(option) => { setFieldValue("crsStepId", option?.value) }}
                        value={stepOptions.find((option) => option.value === values.crsStepId)}
                        placeholder="Select a Step"
                        isSearchable
                        className="mb10"
                        isDisabled={!values.crsStepTypeID}  
                    />
                    </div>
                    {!setup && <ComboBox
                        label="Lesson"
                        className="mb10"
                        options={navSectionTitles.map(title => ({ key: title, text: title }))}
                        text={values.navSectionTitle}
                        onChange={(e, option, index, value) => {
                            if (value) {
                                // When user types a new value
                                setFieldValue("navSectionTitle", value);
                            } else {
                                // When user selects an option
                                setFieldValue("navSectionTitle", option?.text);
                            }
                        }}
                        allowFreeform
                        autoComplete="on"
                    />}
                    <CustomToggle label="Inclusion | Exclusion (Default)*" name="onOffDefault" onText="Include" offText="Exclude"/>
                    <CustomTextField label="Exception Rule*" name="onOffRule" />
                    {steps.filter(x => x.id === values.crsStepId)[0]?.crsStepTypeID === 18 && <CustomToggle label="Primary Poll" name="isPrimaryPoll" />}
                    {steps.filter(x => x.id === values.crsStepId)[0]?.qnsFormatTypeID === 6 && setup && <CustomToggle label="Pre Feeling" name="isPreFeeling" />}
                    {steps.filter(x => x.id === values.crsStepId)[0]?.qnsFormatTypeID === 6 && !setup && <CustomToggle label="Post Feeling" name="isPostFeeling" />}
                    <DialogFooter>
                        <PrimaryButton
                            text="Create"
                            type="submit"
                        />
                        <DefaultButton
                            loading={loading}
                            onClick={toggleShowModal}
                            text="Cancel"
                        />
                    </DialogFooter>
                </Form>
                 )}
            </Formik>
            </div>
        </Modal>
    );
};

const UpdateStepsModal = ({
    showModal,
    toggleShowModal,
    onUpdate,
    courseId,
    course,
    selectedStepId,
    setup
}) => {
    const [{ data: steps, loadingSteps }] = useAxios(`courses/steps`);

    const [stepId, setStepId] = useState(undefined);
    const [crsStepTypeID, setCrsStepTypeID] = useState(undefined);
    const [navSectionTitle, setNavSectionTitle] = useState(undefined);
    const [onOffDefault, setOnOffDefault] = useState(undefined);
    const [onOffRule, setOnOffRule] = useState(undefined);
    const [isPrimaryPoll, setIsPrimaryPoll] = useState(undefined);
    const [isPreFeeling, setIsPreFeeling] = useState(undefined);
    const [isPostFeeling, setIsPostFeeling] = useState(undefined);
    const [stepOptions, setStepOptions] = useState([]);
    const [navSectionTitles, setNavSectionTitles] = useState([]);

    useEffect(() => {
        const step = course?.steps.find(s => s.crS_x_CRSStepID === selectedStepId);
        setStepId(step?.courseStepID);
        setNavSectionTitle(step?.navSectionTitle);
        setOnOffDefault(step?.onOffDefault);
        setOnOffRule(step?.onOffRule);
        setIsPrimaryPoll(step?.isPrimaryPoll);
        setIsPreFeeling(step?.isPreFeeling);
        setIsPostFeeling(step?.isPostFeeling);
        setCrsStepTypeID(step?.courseStepTypeID)
    }, [selectedStepId])
    const [{ data: steptypes, loadingStepTypes }] = useAxios(`courses/steptypes`);


    const [{ loading }, update] = useAxios(
        {
            method: "put",
            url: `/courses/${courseId}/steps/${selectedStepId}`,
        },
        { manual: true }
    );

    useEffect(() => {
        if (steps) {
            setStepOptions(steps.map((step) => ({
                value: step.id,
                label: step.crsStepTypeID ? ('STEPID: ' + step.id + ' ')  + (step.qnsid ? 'QNSID: ' + step.qnsid + ' ' + (step.name ? step.name : '') : (step.name ? step.name : '')) : '',
              })));
              
              const distinctTitles = [
                ...new Set(
                  course.steps
                    .map(step => step.navSectionTitle) // Extract navSectionTitle from each step
                    .filter(title => title !== null && title !== undefined) // Filter out null or undefined values
                )
              ];
              setNavSectionTitles(distinctTitles);

        }
    }, [steps]);

    if (loading || loadingSteps)
        return <Spinner className="absolute-center" label="Loading..." />;

    return (
        <Modal
            isOpen={showModal}
            onDismiss={toggleShowModal}
            isBlocking={true}
            styles={{
                main: {
                    width: '500px', // Explicitly set the width
                    maxWidth: '500px', // Ensure it doesn't exceed this width
                    minWidth: '500px', // Ensure it doesn't shrink below this width
                },
            }}
        >
        <div style={{ padding: '20px', height:500 }}>
            <h2 style={{ marginBottom: '10px', marginTop:0, fontSize:24,fontWeight:'400' }}>Edit</h2>
            <label style={{fontWeight:'500', display:'block', marginBottom: 5}}>Step Type</label>
            <Select
                components={{ MenuList: CustomMenuList }}
                options={steptypes?.map(x => ({ value: x.crsStepTypeID, label: x.name }))}
                onChange={(option) => { setCrsStepTypeID(option?.value) }}
                value={steptypes?.map(x => ({ value: x.crsStepTypeID, label: x.name })).find((option) => option.value === crsStepTypeID)}
                placeholder="Select a Step Type"
                isSearchable
                className="mb10"
            />
            <label style={{fontWeight:'500', display:'block', marginBottom: 5}}>Step</label>
             <Select
                components={{ MenuList: CustomMenuList }}
                options={steps?.filter(step => step.crsStepTypeID === crsStepTypeID).map((step) => ({
                    value: step.id,
                    label: step.crsStepTypeID ? ('STEPID: ' + step.id + ' ')  + (step.qnsid ? 'QNSID: ' + step.qnsid + ' ' + (step.name ? step.name : '') : (step.name ? step.name : '')) : '',
                  }))}
                onChange={(option) => setStepId(option?.value)}
                value={stepOptions.find((option) => option.value === stepId)}
                placeholder="Select a Step"
                isDisabled={!crsStepTypeID} 
                isSearchable
                name="crsStepId"
                className="mb10"
            />
            
            {!setup && <ComboBox
                label="Lesson"
                className=" mb10"
                options={navSectionTitles.map(title => ({ key: title, text: title }))}
                selectedKey={navSectionTitle}
                onChange={(e, option, index, value) => {
                    if (value) {
                        // When user types a new value
                        setNavSectionTitle(value);
                    } else {
                        // When user selects an option
                        setNavSectionTitle(option?.text);
                    }
                }}
                allowFreeform
                autoComplete="on"
            />}
            <Toggle className="" label="Inclusion | Exclusion (Default)*" name="onOffDefault" defaultChecked={onOffDefault}  onText="Include" offText="Exclude" onChange={(e, newValue) => setOnOffDefault(newValue)} />
            <TextField label="Exception Rule*" name="onOffRule" defaultValue={onOffRule} onChange={(e, newValue) => setOnOffRule(newValue)} />

            {steps && steps.filter(x => x.id === stepId)[0]?.crsStepTypeID === 18 && <Toggle label="Primary Poll" name="isPrimaryPoll" defaultChecked={isPrimaryPoll} onChange={(e, newValue) => setIsPrimaryPoll(newValue)} />}
            {steps && steps.filter(x => x.id === stepId)[0]?.qnsFormatTypeID === 6 && setup && <Toggle label="Pre Feeling" name="isPreFeeling" defaultChecked={isPreFeeling} onChange={(e, newValue) => setIsPreFeeling(newValue)} />}
            {steps && steps.filter(x => x.id === stepId)[0]?.qnsFormatTypeID === 6 && !setup && <Toggle label="Post Feeling" name="isPostFeeling" defaultChecked={isPostFeeling} onChange={(e, newValue) => setIsPostFeeling(newValue)} />}
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await update({
                            data: {
                                crS_x_CRSStepID: selectedStepId,
                                crsStepId: stepId,
                                crsStepCategoryID: setup ? 1 : 2,
                                navSectionTitle,
                                onOffDefault,
                                onOffRule,
                                isPrimaryPoll,
                                isPreFeeling,
                                isPostFeeling
                            }
                        });

                        if (!result.isAxiosError) {
                            onUpdate();
                            toggleShowModal();
                        }
                    }}
                    text="Update"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
            </div>
        </Modal>
    );
};

const DeleteStepsModal = ({
    showModal,
    toggleShowModal,
    onDelete,
    courseId,
    selectedStepId
}) => {

    const [{ loading }, deleteItem] = useAxios(
        {
            method: "delete",
            url: `/courses/${courseId}/steps/${selectedStepId}`,
        },
        { manual: true }
    );

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Delete",
                subText: "Delete Step?"
            }}
        >
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await deleteItem();

                        if (!result.isAxiosError) {
                            onDelete();
                            toggleShowModal();
                        }
                    }}
                    text="Delete"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

export default StepsList;